// Account gate shown before the title screen. Login is the intended path
// (server-side saves); if the backend is unreachable the player can fall back to
// local offline play so the game is still usable as a static build.

import { fetchMe, login, register } from '../net/api';
import {
  clearTitlePanel,
  getTitlePanel,
  initTitleShell,
  mkTitleBtn,
  mkTitleInput,
} from './title-screen';

export interface AuthResult {
  mode: 'cloud' | 'offline';
  user: string | null;
}

export async function ensureAuth(): Promise<AuthResult> {
  const existing = await fetchMe();
  if (existing) return { mode: 'cloud', user: existing };

  initTitleShell();

  return new Promise<AuthResult>((resolve) => {
    const panel = getTitlePanel();
    clearTitlePanel();

    const sub = document.createElement('div');
    sub.className = 'merit-title-sub';
    sub.textContent = 'Sign in for cloud saves';
    panel.appendChild(sub);

    const userIn = mkTitleInput('Username', 'text');
    userIn.autocomplete = 'username';
    panel.appendChild(userIn);

    const passIn = mkTitleInput('Password', 'password');
    passIn.autocomplete = 'current-password';
    panel.appendChild(passIn);

    const err = document.createElement('div');
    err.className = 'merit-title-error';
    panel.appendChild(err);

    const row = document.createElement('div');
    row.className = 'merit-title-row';
    panel.appendChild(row);

    const loginBtn = mkTitleBtn('Login');
    const regBtn = mkTitleBtn('Register');
    row.appendChild(loginBtn);
    row.appendChild(regBtn);

    const offline = mkTitleBtn('Play offline', 'secondary');
    panel.appendChild(offline);

    const busy = (on: boolean) => {
      loginBtn.disabled = on;
      regBtn.disabled = on;
      offline.disabled = on;
    };

    const done = (r: AuthResult) => {
      clearTitlePanel();
      resolve(r);
    };

    const attempt = async (fn: (u: string, p: string) => Promise<string>) => {
      err.textContent = '';
      busy(true);
      try {
        const u = await fn(userIn.value.trim(), passIn.value);
        done({ mode: 'cloud', user: u });
      } catch (e) {
        err.textContent = e instanceof Error ? e.message : 'Request failed';
        busy(false);
      }
    };

    loginBtn.addEventListener('click', () => void attempt(login));
    regBtn.addEventListener('click', () => void attempt(register));
    passIn.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') void attempt(login);
    });
    offline.addEventListener('click', () => done({ mode: 'offline', user: null }));

    userIn.focus();
  });
}
